import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useMessageStore } from '../stores/messageStore';
import { useUserStore } from '../stores/userStore';
import ChatInterface from '../components/ChatInterface';
import { getProfilePicture } from '../utils/profileUtils';
import { formatRelativeTime } from '../utils/dateUtils';

const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { users } = useUserStore();
  const { getConversationsByUser, markConversationAsRead } = useMessageStore();
  
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const conversationId = searchParams.get('conversation');
    if (conversationId) {
      setSelectedConversation(conversationId);
      if (user) {
        markConversationAsRead(conversationId, user.id);
      }
    }
  }, [searchParams, user]);

  if (!user) {
    navigate('/login');
    return null;
  }

  const userConversations = getConversationsByUser(user.id)
    .sort((a, b) => {
      const lastMessageA = a.messages[a.messages.length - 1];
      const lastMessageB = b.messages[b.messages.length - 1];
      return new Date(lastMessageB?.timestamp || 0).getTime() - 
             new Date(lastMessageA?.timestamp || 0).getTime();
    });

  const filteredConversations = userConversations.filter(conv => {
    const searchStr = searchTerm.toLowerCase();
    return conv.recipientName.toLowerCase().includes(searchStr) || 
           conv.listingTitle.toLowerCase().includes(searchStr);
  });

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversation(conversationId);
    markConversationAsRead(conversationId, user.id);
  };

  const EmptyState = () => (
    <div className="h-[600px] flex flex-col items-center justify-center text-gray-500">
      <div className="w-48 h-48 mb-6">
        <svg width="100%" height="100%" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="200" height="200" rx="100" fill="#F3F4F6"/>
          <path d="M60 80C60 68.9543 68.9543 60 80 60H120C131.046 60 140 68.9543 140 80V120C140 131.046 131.046 140 120 140H80C68.9543 140 60 131.046 60 120V80Z" fill="#6366F1"/>
          <path d="M75 95C75 91.6863 77.6863 89 81 89H119C122.314 89 125 91.6863 125 95V125C125 128.314 122.314 131 119 131H81C77.6863 131 75 128.314 75 125V95Z" fill="#4F46E5"/>
          <circle cx="100" cy="75" r="10" fill="#4F46E5"/>
          <path d="M85 110H115M85 120H105" stroke="white" strokeWidth="4" strokeLinecap="round"/>
        </svg>
      </div>
      <p className="text-lg font-medium mb-2">No Conversation Selected</p>
      <p className="text-gray-400">Choose a conversation to start messaging</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3">
            {/* Conversations List */}
            <div className="border-r">
              <div className="p-4 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search messages"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="overflow-y-auto h-[600px]">
                {filteredConversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No conversations found
                  </div>
                ) : (
                  filteredConversations.map((conversation) => {
                    const lastMessage = conversation.messages[conversation.messages.length - 1];
                    const otherParticipantId = conversation.participants.find(id => id !== user.id);
                    const profilePic = getProfilePicture(otherParticipantId || '');
                    const unreadCount = conversation.messages.filter(
                      msg => msg.senderId !== user.id && !msg.isRead
                    ).length;

                    return (
                      <div
                        key={conversation.id}
                        onClick={() => handleConversationClick(conversation.id)}
                        className={`w-full p-4 text-left border-b hover:bg-gray-50 relative cursor-pointer ${
                          selectedConversation === conversation.id ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full overflow-hidden">
                              <img
                                src={profilePic}
                                alt="Profile"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-1">
                              <h3 className="font-semibold text-gray-900 truncate">
                                {conversation.recipientName}
                              </h3>
                              <div className="flex items-center">
                                {unreadCount > 0 && (
                                  <span className="inline-flex items-center justify-center h-5 w-5 text-xs font-medium text-white bg-indigo-600 rounded-full">
                                    {unreadCount}
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-indigo-600 hover:text-indigo-700 mb-1">
                              Re: {conversation.listingTitle}
                            </p>
                            <div className="flex justify-between items-center">
                              {lastMessage && (
                                <p className="text-sm text-gray-500 truncate max-w-[70%]">
                                  {lastMessage.content}
                                </p>
                              )}
                              <span className="text-xs text-gray-500">
                                {lastMessage && formatRelativeTime(lastMessage.timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Chat Interface */}
            <div className="col-span-2">
              {selectedConversation ? (
                <ChatInterface
                  conversationId={selectedConversation}
                  onBack={() => setSelectedConversation(null)}
                />
              ) : (
                <EmptyState />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;